import { useRef, useCallback, useState } from 'react'
import { X, Download, Copy, Check, Twitter, Facebook, Linkedin, AudioWaveform } from 'lucide-react'
import html2canvas from 'html2canvas'
import type { ArtifactReport } from '../lib/analyzer'
import { ScoreCircle } from './ScoreCircle'
import {
  getSizzleSeverity,
  getMuddinessSeverity,
  getClickSeverity,
  getLufsSeverity,
} from '../utils/scoring'

interface ShareCardProps {
  report: ArtifactReport
  fileName: string
  onClose: () => void
}

export function ShareCard({ report, fileName, onClose }: ShareCardProps) {
  const cardRef = useRef<HTMLDivElement>(null)
  const [copied, setCopied] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const shareUrl = `https://scan.luminary.tools?utm_source=share&utm_medium=social`

  const handleDownload = useCallback(async () => {
    if (!cardRef.current) return

    setDownloading(true)
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#0a0f14', // forge-bg-deep
        scale: 2,
      })

      const link = document.createElement('a')
      link.download = `forge-scan-${fileName.replace(/\.[^/.]+$/, '')}.png`
      link.href = canvas.toDataURL('image/png')
      link.click()
    } catch (error) {
      console.error('Failed to download:', error)
    } finally {
      setDownloading(false)
    }
  }, [fileName])

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }, [shareUrl])

  const shareText = `My AI track scored ${report.overallScore}/100 on Forge Scanner! Check your tracks:`

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`

  const sizzleSeverity = getSizzleSeverity(report.sizzleScore)
  const muddinessSeverity = getMuddinessSeverity(report.muddinessScore)
  const clickSeverity = getClickSeverity(report.clickCount)
  const lufsSeverity = getLufsSeverity(report.lufs)

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'good':
        return 'text-[hsl(168_76%_42%)]' // forge-success
      case 'moderate':
        return 'text-[hsl(45_93%_47%)]' // forge-warning
      case 'high':
        return 'text-[hsl(38_92%_50%)]' // forge-copper
      case 'critical':
        return 'text-[hsl(0_72%_51%)]' // forge-danger
      default:
        return 'text-white'
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-[hsl(220_33%_10%)] rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-[hsl(220_20%_20%)]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[hsl(220_20%_20%)]">
          <h2 className="text-lg font-semibold text-white">Share Your Results</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[hsl(220_25%_14%)] rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-[hsl(220_13%_65%)]" />
          </button>
        </div>

        {/* Shareable card preview */}
        <div className="p-4">
          <div
            ref={cardRef}
            className="rounded-xl p-6 border border-[hsl(220_20%_20%)]"
            style={{
              background: 'linear-gradient(135deg, hsl(220 25% 14%) 0%, hsl(222 47% 6%) 100%)',
            }}
          >
            {/* Card header with logo */}
            <div className="flex items-center gap-2 mb-5">
              <div className="p-1.5 bg-gradient-to-br from-[hsl(38_92%_50%)] to-[hsl(38_70%_35%)] rounded-lg">
                <AudioWaveform className="w-4 h-4 text-[hsl(222_47%_6%)]" />
              </div>
              <span className="text-sm font-semibold">
                <span className="text-[hsl(38_92%_50%)]">Forge</span>
                <span className="text-[hsl(220_13%_65%)]"> / Scanner</span>
              </span>
            </div>

            <div className="flex items-center gap-4 mb-5">
              <ScoreCircle score={report.overallScore} size="sm" />
              <div>
                <h3 className="text-lg font-semibold text-white">
                  Quality Score
                </h3>
                <p className="text-xs text-[hsl(220_13%_45%)] truncate max-w-[180px]">
                  {fileName}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4">
              <div className="bg-[hsl(220_20%_18%)] rounded-lg p-3">
                <p className="text-[10px] text-[hsl(220_13%_45%)] uppercase tracking-wide mb-0.5">Sizzle</p>
                <p className={`text-sm font-medium ${getSeverityColor(sizzleSeverity.severity)}`}>
                  {sizzleSeverity.label}
                </p>
              </div>
              <div className="bg-[hsl(220_20%_18%)] rounded-lg p-3">
                <p className="text-[10px] text-[hsl(220_13%_45%)] uppercase tracking-wide mb-0.5">Muddiness</p>
                <p className={`text-sm font-medium ${getSeverityColor(muddinessSeverity.severity)}`}>
                  {muddinessSeverity.label}
                </p>
              </div>
              <div className="bg-[hsl(220_20%_18%)] rounded-lg p-3">
                <p className="text-[10px] text-[hsl(220_13%_45%)] uppercase tracking-wide mb-0.5">Clicks</p>
                <p className={`text-sm font-medium ${getSeverityColor(clickSeverity.severity)}`}>
                  {clickSeverity.label}
                </p>
              </div>
              <div className="bg-[hsl(220_20%_18%)] rounded-lg p-3">
                <p className="text-[10px] text-[hsl(220_13%_45%)] uppercase tracking-wide mb-0.5">Loudness</p>
                <p className={`text-sm font-medium ${getSeverityColor(lufsSeverity.severity)}`}>
                  {report.lufs.toFixed(1)} LUFS
                </p>
              </div>
            </div>

            <div className="text-center pt-3 border-t border-[hsl(220_20%_20%)]">
              <p className="text-[10px] text-[hsl(220_13%_45%)]">
                Scan your AI music free at{' '}
                <span className="text-[hsl(38_92%_50%)] font-medium">scan.luminary.tools</span>
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-[hsl(220_20%_20%)] space-y-3">
          {/* Download button */}
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="forge-btn-primary w-full justify-center"
          >
            <Download className="w-4 h-4" />
            {downloading ? 'Downloading...' : 'Download as Image'}
          </button>

          {/* Copy link */}
          <button
            onClick={handleCopyLink}
            className="forge-btn-secondary w-full justify-center"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-[hsl(168_76%_42%)]" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Copy Link
              </>
            )}
          </button>

          {/* Social share buttons */}
          <div className="flex items-center justify-center gap-3 pt-2">
            <a
              href={twitterUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-[hsl(220_20%_18%)] hover:bg-[hsl(220_25%_14%)] border border-[hsl(220_20%_20%)] rounded-xl transition-colors"
            >
              <Twitter className="w-5 h-5 text-[hsl(220_13%_65%)]" />
            </a>
            <a
              href={facebookUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-[hsl(220_20%_18%)] hover:bg-[hsl(220_25%_14%)] border border-[hsl(220_20%_20%)] rounded-xl transition-colors"
            >
              <Facebook className="w-5 h-5 text-[hsl(220_13%_65%)]" />
            </a>
            <a
              href={linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-[hsl(220_20%_18%)] hover:bg-[hsl(220_25%_14%)] border border-[hsl(220_20%_20%)] rounded-xl transition-colors"
            >
              <Linkedin className="w-5 h-5 text-[hsl(220_13%_65%)]" />
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
